import React, { ReactNode, useEffect, useState } from 'react';
import BrInputBox from '@components/BrInputBox';
import BrQueryBox from '@components/BrQueryBox';
import BrSelect from '@components/BrSelect';
import BrRadioButton from '@components/BrRadioButton';
import BrIcons from '@components/BrIcons';
import './brConditionBuilder.scss';

enum OPERATOR {
  OR = 'OR',
  AND = 'AND',
}
enum SUPPORTED_TYPES {
  SELECT = 'select',
  QUERY_BOX = 'query_box',
  INPUT = 'input',
  RANGE_INPUT = 'range_input',
}
interface OptionProps {
  title: string;
  value: string;
  [key: string]: any;
}
interface TriggerProps {
  type: SUPPORTED_TYPES;
  placeholder?: string;
  [key: string]: any;
}
interface InnerOperatorProps {
  key: string;
  title: string;
  trigger: TriggerProps;
}
interface OperandProps {
  key: string;
  title: string;
  operatorPlaceholder?: string;
  defaultTrigger: TriggerProps;
  innerOperators: Array<InnerOperatorProps>;
  group?: string;
  options?: Array<OptionProps>;
  fetchSuggestions?: any;
}
interface BrConditionBuilderProps {
  onChangeCallback: any;
  defaultPlaceholders?: Array<string>;
  operandProps: Array<OperandProps>;
}
interface Operand {
  key: string;
  selectedOperator?: string;
  value?: any;
  values?: any;
}
interface OperandGroup {
  operator: OPERATOR;
  operands: Array<Operand | null>;
}
interface OverallConfig {
  operator: OPERATOR;
  operands: Array<OperandGroup>;
}

export default function BrConditionBuilder({
  configProps,
  initializerProps,
}: {
  configProps: BrConditionBuilderProps;
  initializerProps: OverallConfig | undefined;
}) {
  const { onChangeCallback, operandProps = [], defaultPlaceholders = [] } = configProps;
  const [baseOperator, setBaseOperator] = useState<OPERATOR>(OPERATOR.OR);
  const [operandGroupArray, setOperandGroupArray] = useState<Array<OperandGroup>>([]);
  useEffect(() => {
    const getInitialConfigState = (): Array<OperandGroup> => {
      if (initializerProps) {
        return initializerProps.operands.map(
          (operand): OperandGroup => ({
            operator: operand.operator,
            operands: operand.operands,
          }),
        );
      }
      return [];
    };
    const newOperandGroupArray = getInitialConfigState();
    setBaseOperator(initializerProps?.operator || OPERATOR.OR);
    setOperandGroupArray(newOperandGroupArray);
  }, [initializerProps]);
  const baseOptions = operandProps.map((config) => {
    return {
      value: config.key,
      title: config.title,
      group: config.group,
    };
  });
  useEffect(() => {
    const getCallbackObject = ({
      currOperands,
      operator,
    }: {
      currOperands: Array<OperandGroup>;
      operator?: OPERATOR;
    }) => {
      return {
        operator: operator || baseOperator,
        operands: currOperands,
      };
    };
    onChangeCallback(getCallbackObject({ currOperands: operandGroupArray, operator: baseOperator }));
  }, [baseOperator, operandGroupArray, onChangeCallback]);

  const handleAddOperand = () => {
    setOperandGroupArray([...operandGroupArray, { operator: OPERATOR.OR, operands: [null] }]);
  };

  const handleDeleteOperand = (operandGroupArrayIndex: any, operandIndex: any) => {
    const currentOperandGroupArray = [...operandGroupArray];
    const groupConfig = currentOperandGroupArray[operandGroupArrayIndex];
    const operands = groupConfig.operands;
    operands.splice(operandIndex, 1);
    if (operands.length === 0) {
      currentOperandGroupArray.splice(operandGroupArrayIndex, 1);
    }
    setOperandGroupArray(currentOperandGroupArray);
    // onChangeCallback(getCallbackObject({ currOperands: currentOperandGroupArray }));
  };

  const addNewOperand = (key: string, operandGroupArrayIndex: any, operandIndex: any) => {
    const newOperand = { key };
    const currOperandGroupArray = [...operandGroupArray];
    currOperandGroupArray[operandGroupArrayIndex].operands[operandIndex] = newOperand;
    setOperandGroupArray(currOperandGroupArray);
  };

  const getSingleElement = (
    key: string,
    currentTrigger: TriggerProps | undefined,
    operandGroupArrayIndex: any,
    operandIndex: any,
    options: Array<any> | undefined,
    fetchSuggestions: any,
    isDefault: boolean,
    isBaseType: boolean,
    isOperator: boolean,
  ): ReactNode => {
    let element = <></>;
    if (!currentTrigger) {
      return element;
    }
    const { placeholder, ...extraProps } = currentTrigger;
    const type: SUPPORTED_TYPES = isDefault || isOperator ? SUPPORTED_TYPES.SELECT : currentTrigger.type;
    switch (type) {
      case SUPPORTED_TYPES.SELECT: {
        let value = '';
        if (isBaseType || isOperator) {
          value = key;
        } else if (!isDefault) {
          value = operandGroupArray[operandGroupArrayIndex].operands[operandIndex]?.value;
        }
        let onSelect: any;
        if (isBaseType) {
          onSelect = (value: any) => addNewOperand(value, operandGroupArrayIndex, operandIndex);
        } else if (isDefault) {
          onSelect = () => undefined;
        } else {
          onSelect = (value: any) => {
            const currOperandGroupArray = [...operandGroupArray];
            const currentOperand = currOperandGroupArray[operandGroupArrayIndex].operands[operandIndex];
            if (currentOperand) {
              if (isOperator) {
                currentOperand.selectedOperator = value;
                currentOperand.value = '';
                currentOperand.values = [];
              } else {
                currentOperand.value = value;
              }
              setOperandGroupArray(currOperandGroupArray);
              // onChangeCallback(getCallbackObject({ currOperands: currOperandGroupArray }));
            }
          };
        }
        element = (
          <BrSelect
            selected={value}
            options={options || []}
            placeholder={placeholder || 'Select'}
            onSelect={onSelect}
            allowSearch={!isOperator}
            searchPlaceholder="Search..."
            hasGroupedOptions={isBaseType}
          />
        );
        break;
      }
      case SUPPORTED_TYPES.INPUT: {
        const value: string = operandGroupArray[operandGroupArrayIndex].operands[operandIndex]?.value;
        const onChangeInput = (e: any) => {
          const currOperandGroupArray = [...operandGroupArray];
          const currentOperand = currOperandGroupArray[operandGroupArrayIndex].operands[operandIndex];
          if (currentOperand) {
            currentOperand.value = e.target.value;
            setOperandGroupArray(currOperandGroupArray);
          }
        };
        element = (
          <BrInputBox
            className="br-cb-input-box"
            placeholder={placeholder || 'Input'}
            value={value}
            onChange={onChangeInput}
          />
        );
        break;
      }
      case SUPPORTED_TYPES.QUERY_BOX: {
        const values: Array<any> = operandGroupArray[operandGroupArrayIndex].operands[operandIndex]?.values;
        const handleQuerySubmit = (selectedOption: OptionProps) => {
          if (!selectedOption) {
            return;
          }
          if (values?.includes(selectedOption.value)) {
            return;
          }
          const currOperandGroupArray = [...operandGroupArray];
          const currentOperand = currOperandGroupArray[operandGroupArrayIndex].operands[operandIndex];
          if (currentOperand && !currentOperand.values) {
            currentOperand.values = [];
          }
          currentOperand?.values.push(selectedOption.value);
          setOperandGroupArray(currOperandGroupArray);
        };
        const suggestionFormatter = (val: OptionProps) => {
          return <span>{val.title}</span>;
        };
        const queryFormatter = (query: any) => {
          const selectedOption = options?.find((option: OptionProps) => option.value === query);
          return selectedOption?.title || query;
        };
        const preSubmitDecorator = (query: any, matchedOptions: Array<OptionProps>) => {
          const currOptions = options || matchedOptions;
          const selectedOption = currOptions?.find(
            (option: OptionProps) => option.title === query || option.value === query,
          );
          return selectedOption;
        };
        const onRemoveQuery = (query: any) => {
          const removeIndex = values?.indexOf(query);
          values.splice(removeIndex, 1);
          const currOperandGroupArray = [...operandGroupArray];
          const currentOperand = currOperandGroupArray[operandGroupArrayIndex].operands[operandIndex];
          if (currentOperand) {
            currentOperand.values = values;
          }
          setOperandGroupArray(currOperandGroupArray);
        };
        const suggestionComparator = (query: any, suggestion: any) => {
          if (
            suggestion.title.toLowerCase().includes(query.trim().toLowerCase())
            || suggestion.value.toLowerCase().includes(query.trim().toLowerCase())
          ) {
            return true;
          }
          return false;
        };
        element = (
          <BrQueryBox
            className="br-cb-query-box"
            placeholder={placeholder}
            queryList={values || []}
            queryFormatter={queryFormatter}
            onSubmit={handleQuerySubmit}
            suggestionList={options}
            suggestionFormatter={suggestionFormatter}
            suggestionComparator={suggestionComparator}
            preSubmitDecorator={preSubmitDecorator}
            onRemoveQuery={onRemoveQuery}
            fetchSuggestion={fetchSuggestions}
            {...extraProps}
          />
        );
        break;
      }
      case SUPPORTED_TYPES.RANGE_INPUT: {
        const values: Array<any> = operandGroupArray[operandGroupArrayIndex].operands[operandIndex]?.values || [0, 0];
        const onChangeInput = (e: any, index: number) => {
          const currOperandGroupArray = [...operandGroupArray];
          const currentOperand = currOperandGroupArray[operandGroupArrayIndex].operands[operandIndex];
          if (currentOperand) {
            if (!currentOperand.values) {
              currentOperand.values = [];
            }
            currentOperand.values[index] = e.target.value;
            setOperandGroupArray(currOperandGroupArray);
          }
        };
        element = (
          <div className="br-cb-range-input">
            <BrInputBox
              className="br-cb-input-box range-input-left"
              placeholder="from"
              value={values?.[0]}
              onChange={(e: any) => onChangeInput(e, 0)}
            />
            -
            <BrInputBox
              className="br-cb-input-box range-input-right"
              placeholder="to"
              value={values?.[1]}
              onChange={(e: any) => onChangeInput(e, 1)}
            />
          </div>
        );
        break;
      }
      default:
        break;
    }
    return element;
  };

  const getBaseTypeElement = (key: string, operandGroupArrayIndex: any, operandIndex: any): ReactNode => {
    const currentTrigger: TriggerProps = {
      type: SUPPORTED_TYPES.SELECT,
      placeholder: defaultPlaceholders?.[0] || 'Select a type',
    };
    return getSingleElement(
      key,
      currentTrigger,
      operandGroupArrayIndex,
      operandIndex,
      baseOptions,
      undefined,
      true,
      true,
      false,
    );
  };

  const getOperandDisplay = (operand: Operand | null, operandGroupArrayIndex: any, operandIndex: any): ReactNode => {
    let baseTypeElement: ReactNode = <></>;
    let operatorDisplay: ReactNode = <></>;
    let inputElementDisplay: ReactNode = <></>;
    if (!operand) {
      baseTypeElement = getBaseTypeElement('', operandGroupArrayIndex, operandIndex);
      operatorDisplay = defaultPlaceholders?.[1]
      && getSingleElement(
        '',
        {
          type: SUPPORTED_TYPES.SELECT,
          placeholder: defaultPlaceholders[2],
        },
        operandGroupArrayIndex,
        operandIndex,
        [],
        undefined,
        true,
        false,
        false,
      );
      inputElementDisplay = defaultPlaceholders?.[2]
      && getSingleElement(
        '',
        {
          type: SUPPORTED_TYPES.SELECT,
          placeholder: defaultPlaceholders[2],
        },
        operandGroupArrayIndex,
        operandIndex,
        [],
        undefined,
        true,
        false,
        false,
      );
    } else {
      baseTypeElement = getBaseTypeElement(operand.key, operandGroupArrayIndex, operandIndex);
      const currentOperandProps = operandProps.find((operandProp) => operandProp.key === operand.key);

      const customOperatorOptions = currentOperandProps?.innerOperators.map((innerOperator) => {
        return {
          value: innerOperator.key,
          title: innerOperator.title,
          trigger: innerOperator.trigger,
        };
      });

      operatorDisplay = getSingleElement(
        operand.selectedOperator || '',
        {
          type: SUPPORTED_TYPES.SELECT,
          placeholder: currentOperandProps?.operatorPlaceholder,
        },
        operandGroupArrayIndex,
        operandIndex,
        customOperatorOptions || [],
        undefined,
        false,
        false,
        true,
      );
      const selectedTrigger = currentOperandProps?.innerOperators.find(
        (innerOperator) => innerOperator.key === operand.selectedOperator,
      )?.trigger || currentOperandProps?.defaultTrigger;
      inputElementDisplay = getSingleElement(
        '',
        selectedTrigger,
        operandGroupArrayIndex,
        operandIndex,
        currentOperandProps?.options,
        currentOperandProps?.fetchSuggestions,
        false,
        false,
        false,
      );
    }
    return (
      <div className="br-cb-config-display">
        {baseTypeElement}
        {operatorDisplay}
        {inputElementDisplay}
        <BrIcons.BrBin onClick={() => handleDeleteOperand(operandGroupArrayIndex, operandIndex)} tooltipText="Remove" />
      </div>
    );
  };

  const getOperatorDisplay = (operator: OPERATOR): ReactNode => {
    return (
      <div className="br-cb-separator">
        <div className="br-cb-separator-text">{operator.toLocaleUpperCase()}</div>
        <div className="br-cb-separator-line" />
      </div>
    );
  };

  const getRadioButton = (operator: OPERATOR, label: any, id: string): ReactNode => {
    return (
      <BrRadioButton
        onClick={() => {
          setBaseOperator(operator);
        }}
        id={id}
        isSelected={baseOperator === operator}
        label={label}
      />
    );
  };

  return (
    <div className="br-condition-builder">
      <div className="br-cb-radio-group">
        {getRadioButton(
          OPERATOR.OR,
          <span>
            {'Match '}
            <b>ANY</b>
            {' of the following'}
          </span>,
          'br-condition-builder-1',
        )}
        {getRadioButton(
          OPERATOR.AND,
          <span>
            {'Match '}
            <b>ALL</b>
            {' of the following'}
          </span>,
          'br-condition-builder-2',
        )}
      </div>
      {operandGroupArray.map((operandGroup, groupConfigIndex) => {
        const baseOperatorDisplay = getOperatorDisplay(baseOperator);
        const groupOperandsDisplay = operandGroup.operands.map((operand, operandIndex) => {
          const innerOperatorDisplay = getOperatorDisplay(operandGroup.operator);
          const innerOperandsDisplay = getOperandDisplay(operand, groupConfigIndex, operandIndex);
          return (
            <div>
              {operandIndex !== 0 && innerOperatorDisplay}
              {innerOperandsDisplay}
            </div>
          );
        });
        return (
          <div className="br-cb-builder-area">
            {groupConfigIndex !== 0 && baseOperatorDisplay}
            {groupOperandsDisplay}
          </div>
        );
      })}
      <div className="br-cb-add-button" onClick={handleAddOperand}>
        + Add filter condition
      </div>
    </div>
  );
}
